import { buildElationUrlFeedItem } from '@seen/core/elation'
import { featureFlags } from '@seen/core/featureFlags'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { InboxItemType } from '@seen/core/models/inbox'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { resolveId } from '@seen/core/references'
import {
  getFilenameFromContentDisposition,
  now,
  saveBlobWithName,
} from '@seen/core/utils'
import { Button, IconStandingOrder } from '@seen/ui'
import {
  IconEdit,
  IconExternalLink,
  IconFileExport,
  IconLayoutColumns,
  IconLayoutRows,
  IconMaximize,
  IconRepeat,
  IconSparkles,
  IconTrashX,
} from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { type JSX, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuSeparator } from '#src/components/core/dropdown-menu/DropdownMenuSeparator.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { DropdownMenuQuestLinkItem } from '#src/components/MenuQuestLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { OrderForm } from '#src/components/order/order-form/OrderForm.tsx'
import { OrderCreatedToast } from '#src/components/order/OrderCreatedToast.tsx'
import { OrderDisplay } from '#src/components/order/OrderDisplay.tsx'
import {
  OrderPriorityCombobox,
  OrderPriorityIconButton,
} from '#src/components/order/OrderPriorityCombobox.tsx'
import { OrderStatusCombobox } from '#src/components/order/OrderStatusCombobox.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useConfig } from '#src/contexts/config/useConfig.ts'
import { ParticipantReferenceContext } from '#src/contexts/current-participant/ParticipantReferenceContext.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useMarkRelatedInboxItemsAsRead } from '#src/hooks/inbox/useMarkRelatedInboxItemsAsRead.ts'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { toast } from '#src/utils/toast.ts'

interface OrderPageProps {
  orderId?: string
  hideMaximize?: boolean
}

export function OrderPage({
  orderId: propOrderId,
  hideMaximize = false,
}: OrderPageProps): JSX.Element {
  const { orderId: paramOrderId, patientId } = useParams<{
    orderId?: string
    patientId?: string
  }>()
  const orderId = propOrderId ?? paramOrderId
  const config = useConfig()
  const [isEdit, setIsEdit] = useState(false)
  const navigate = useNavigate()
  const participantScopedReorderEnabled = useFeatureFlag(
    featureFlags.orderReorderParticipantScoped
  )

  const { open: openConfirmWithAnnotation } = useConfirmWithAnnotationStore()
  const isOrderDisplayTwoColumn = usePortalStore(
    (state) => state.isOrderDisplayTwoColumn
  )
  const toggleOrderDisplayLayout = usePortalStore(
    (state) => state.toggleOrderDisplayLayout
  )
  const showSuggestedOrderDocuments = usePortalStore(
    (state) => state.showSuggestedOrderDocuments
  )
  const toggleShowSuggestedOrderDocuments = usePortalStore(
    (state) => state.toggleShowSuggestedOrderDocuments
  )

  const { data: order, isLoading: isOrderLoading } = useRead(Order, orderId, {
    query: { enabled: !!orderId },
  })
  const practitioner = useCurrentPractitioner()
  const createCommunication = useCreateMutation<CommentCommunication>({
    disableDefaultErrorToast: true,
  })
  const patchOrder = usePatchMutation<Order>()

  const markRelatedInboxItemsAsRead = useMarkRelatedInboxItemsAsRead({
    silent: true,
  })

  useEffect(() => {
    if (order) {
      markRelatedInboxItemsAsRead(
        (item) =>
          item.data.type === InboxItemType.OrderSigned &&
          item.data.order.reference === order.reference.reference
      )
    }
  }, [order, markRelatedInboxItemsAsRead])

  const seenApi = useSeenApi().seenApi
  const generateReferralPdfMutation = useMutation<unknown, unknown, Order>({
    mutationFn: async (o) => {
      const response = await seenApi.post('/orders/generate-pdf', {
        orderId: o.id,
      })

      const binaryData = atob(response.data)
      const bytes = new Uint8Array(binaryData.length)
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i)
      }

      const blob = new Blob([bytes], { type: 'application/pdf' })
      saveBlobWithName(blob, `referral-${o.seenIdentifier}.pdf`)
    },
    onMutate: (o) => {
      toast.loading(`Exporting referral…`, {
        id: `export-referral-${o.seenIdentifier}`,
      })
    },
    onSettled: (_, __, o) => {
      toast.dismiss(`export-referral-${o.seenIdentifier}`)
    },
    onError: (error) => {
      toast.error(`Failed to export referral: ${error}`)
    },
  })

  const exportPdfMutation = useMutation<unknown, unknown, Order>({
    mutationFn: async (o) => {
      const response = await seenApi.post(
        `/pdf/orders/${o.id}`,
        {},
        { responseType: 'blob' }
      )
      const filename = getFilenameFromContentDisposition(
        response.headers['content-disposition']
      )
      saveBlobWithName(
        response.data,
        filename ?? `order-${o.seenIdentifier}.pdf`
      )
    },
    onMutate: (o) => {
      toast.loading(`Exporting order…`, {
        id: `export-order-${o.seenIdentifier}`,
      })
    },
    onSettled: (_, __, o) => {
      toast.dismiss(`export-order-${o.seenIdentifier}`)
    },
    onError: (error) => {
      toast.error(`Failed to export order: ${error}`)
    },
  })

  const participantQuery = useRead(Participant, order?.participant)
  const participantElationId = participantQuery.data?.elationId
  const orderElationId = order?.elationId
  const elationLink =
    participantElationId &&
    orderElationId &&
    buildElationUrlFeedItem({
      elationBaseUrl: config.elationBaseUrl,
      elationPatientId: participantElationId,
      elationFeedItemId: orderElationId,
    })

  if (isOrderLoading) {
    return <Loader />
  }

  if (!order) {
    return <div>Order not found</div>
  }

  return (
    <PageLayout contentClassName="mx-auto w-full gap-0 p-0 px-0!">
      <ParticipantReferenceContext.Provider value={order.participant}>
        <div className="sticky top-0 mb-4 flex items-center justify-between gap-2">
          <OrderStatusCombobox order={order} />
          <ResourceFieldForm
            instance={order}
            valueFn={(o) => o.priority}
            patchFn={(o, value) => {
              o.priority = value ?? undefined
            }}
            render={({ field: { value, onChange, ...rest } }) => (
              <OrderPriorityCombobox
                onSelect={onChange}
                selected={value ?? null}
              >
                <OrderPriorityIconButton priority={value ?? null} {...rest} />
              </OrderPriorityCombobox>
            )}
          />
          {order.standing && <IconStandingOrder />}
          <div className="flex flex-1 items-center gap-2">
            {order.seenIdentifier && (
              <p className="text-muted-foreground underline">
                {order.seenIdentifier}
              </p>
            )}
            <p className="font-bold text-muted-foreground">
              {order.categoryCodeDisplay}
            </p>
            <ResourceFieldForm
              className="flex-1"
              instance={order}
              valueFn={(o) => o.title}
              patchFn={(o, v) => {
                o.title = v ?? undefined
              }}
              render={({ field: { value, ...rest } }) => (
                <RichTextEditor
                  {...rest}
                  defaultValue={value ?? getOrderTitle(order).slice(0, 85)}
                  variant="underline"
                  placeholder="Title"
                />
              )}
              debounceTyping
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!hideMaximize && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/Patient/${resolveId(order.participant)}/orders/${
                          order.id
                        }`
                      )
                    }
                    className="mr-2"
                  >
                    <IconMaximize size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open full page view</TooltipContent>
              </Tooltip>
            )}
            <MoreDropdownMenu>
              {!isEdit && (
                <DropdownMenuItem
                  disabled={!order.isEditable}
                  onClick={() => setIsEdit(true)}
                >
                  <IconEdit size={14} /> Edit Order
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  const reorderBaseUrl =
                    participantScopedReorderEnabled && patientId
                      ? `/Patient/${patientId}/orders`
                      : '/orders'
                  const reorderUrl = `${reorderBaseUrl}/new?reorderId=${order.id}`
                  void navigate(reorderUrl)
                }}
              >
                <IconRepeat size={14} />
                Re-order
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!order.isRevokable}
                onClick={() =>
                  openConfirmWithAnnotation({
                    title: 'Are you sure you want to revoke this Order?',
                    description:
                      'This action cannot be undone. This will revoke the order.',
                    onConfirm: (note: string) =>
                      patchOrder.mutate(
                        {
                          instance: order,
                          mutateFn: (resource) => resource.revoke(),
                        },
                        {
                          onSuccess: () => {
                            toast.success('Order successfully revoked.')
                            if (note.trim()) {
                              createCommunication.mutate(
                                new CommentCommunication({
                                  message: note,
                                  sender: practitioner.reference,
                                  subject: order.participant,
                                  partOf: [order.reference],
                                  orderRevokeReason: true,
                                  sent: now(),
                                }),
                                {
                                  onError: () =>
                                    toast.error(
                                      'Order revoked, but failed to save the revoke reason.'
                                    ),
                                }
                              )
                            }
                          },
                        }
                      ),
                  })
                }
              >
                <IconTrashX size={14} />
                Revoke Order
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => generateReferralPdfMutation.mutate(order)}
                disabled={generateReferralPdfMutation.isPending}
              >
                <IconFileExport size={14} />
                {generateReferralPdfMutation.isPending
                  ? 'Exporting…'
                  : 'Export Referral'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportPdfMutation.mutate(order)}
                disabled={exportPdfMutation.isPending}
              >
                <IconFileExport size={14} />
                {exportPdfMutation.isPending ? 'Exporting…' : 'Export Order'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleOrderDisplayLayout}>
                {isOrderDisplayTwoColumn ? (
                  <IconLayoutRows size={14} />
                ) : (
                  <IconLayoutColumns size={14} />
                )}
                {isOrderDisplayTwoColumn
                  ? 'Single column layout'
                  : 'Two column layout'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleShowSuggestedOrderDocuments}>
                <IconSparkles size={14} />
                {showSuggestedOrderDocuments
                  ? 'Hide suggested documents'
                  : 'Show suggested documents'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {!!elationLink && (
                <DropdownMenuItem asChild>
                  <a
                    href={elationLink}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2"
                  >
                    <IconExternalLink size={14} />
                    Open in Elation
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuMedplumLinkItem to={order} />
              <DropdownMenuQuestLinkItem to={order} />
            </MoreDropdownMenu>
          </div>
        </div>
        <div>
          {isEdit && (
            <OrderForm
              order={order}
              onCancel={() => setIsEdit(false)}
              onSubmit={(newOrder, options) => {
                setIsEdit(false)
                // Skip the generic "Order created" toast for deferred (async)
                // submissions — LabSubmissionWatcher surfaces the terminal
                // toast once the subscription handler completes.
                if (!options?.deferred) {
                  toast.success(<OrderCreatedToast order={newOrder} />)
                }
              }}
            />
          )}
          {!isEdit && (
            <OrderDisplay
              order={order}
              isTwoColumn={isOrderDisplayTwoColumn}
              showSuggestedOrderDocuments={showSuggestedOrderDocuments}
              onEditOrder={() => setIsEdit(true)}
            />
          )}
        </div>
      </ParticipantReferenceContext.Provider>
    </PageLayout>
  )
}
